import { useEffect, useMemo, useRef, useState } from "react";
import { LumikaLogo } from "../components/LumikaLogo";

type GoogleButtonStatus = "loading" | "ready" | "missing-client-id" | "unavailable";

type GoogleIdentity = {
  initialize: (config: {
    client_id: string;
    login_uri: string;
    ux_mode: "redirect";
    auto_select: boolean;
    hd: string;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: "standard";
      theme: "filled_black";
      size: "large";
      shape: "rectangular";
      text: "signin_with";
      logo_alignment: "left";
      width: number;
    },
  ) => void;
};

declare global {
  interface Window {
    __lumikaGoogleIdentityConfigKey?: string;
    google?: {
      accounts?: {
        id?: GoogleIdentity;
      };
    };
  }
}

export function SignInPage() {
  const error = new URLSearchParams(window.location.search).get("error");
  const loginUri = useMemo(() => `${window.location.origin}/google/auth/callback`, []);
  const clientId = import.meta.env.VITE_ADMIN_GOOGLE_CLIENT_ID ?? "";
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonStatus, setButtonStatus] = useState<GoogleButtonStatus>("loading");

  useEffect(() => {
    if (!clientId) {
      setButtonStatus("missing-client-id");
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    const renderGoogleButton = () => {
      const parent = buttonRef.current;
      const googleIdentity = window.google?.accounts?.id;

      if (cancelled || !parent) return;

      if (!googleIdentity) {
        attempts += 1;
        if (attempts >= 80) {
          setButtonStatus("unavailable");
          return;
        }
        timer = window.setTimeout(renderGoogleButton, 100);
        return;
      }

      try {
        parent.innerHTML = "";
        const configKey = `${clientId}|${loginUri}|admin`;
        if (window.__lumikaGoogleIdentityConfigKey !== configKey) {
          googleIdentity.initialize({
            client_id: clientId,
            login_uri: loginUri,
            ux_mode: "redirect",
            auto_select: false,
            hd: "lumika.ai",
          });
          window.__lumikaGoogleIdentityConfigKey = configKey;
        }
        googleIdentity.renderButton(parent, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
          width: Math.min(parent.clientWidth || 360, 360),
        });
        setButtonStatus("ready");
      } catch {
        setButtonStatus("unavailable");
      }
    };

    renderGoogleButton();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (buttonRef.current) buttonRef.current.innerHTML = "";
    };
  }, [clientId, loginUri]);

  return (
    <main className="grid min-h-screen place-items-center bg-foreground p-6 text-background">
      <section className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <LumikaLogo theme="auto" size={48} />
        <div className="grid gap-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-background/60">
            lumika
          </p>
          <h1 className="text-4xl font-semibold tracking-tight">Admin</h1>
          <p className="text-sm text-background/70">
            Onboard dealerships, configure rooftops, and invite the first operators.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm text-background"
          >
            Access was not granted. Use a verified @lumika.ai Google account.
          </div>
        )}

        <div
          ref={buttonRef}
          aria-live="polite"
          className="flex min-h-[44px] w-full items-center justify-center"
        />

        {buttonStatus === "loading" && (
          <p className="text-xs text-background/60">Loading Google sign-in…</p>
        )}
        {buttonStatus === "missing-client-id" && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Google sign-in is not configured for this admin app.
          </div>
        )}
        {buttonStatus === "unavailable" && (
          <div
            role="alert"
            className="w-full rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-sm"
          >
            Google sign-in could not load. Check your browser network settings and try again.
          </div>
        )}
      </section>
    </main>
  );
}
